/**
 * The organization picker's tree, as pure functions (no React, no i18n) —
 * "which organizations get this connector" (item 65A). `orgs` is the flat
 * list the server sends (each with its parent); the picker works on the
 * tree it makes.
 *
 * Ticking an organization ticks it and everything under it; unticking does
 * the same. An organization's box is ticked when it and every organization
 * under it is chosen, empty when none is, and partial otherwise — so a
 * parent can still be chosen alone (untick its children) and shows it.
 *
 * Search keeps the tree's context: a match is shown with every organization
 * above it (so you see where it sits), and those are opened.
 *
 * `tests/unit/connector_library/org_tree.test.mjs` runs the real code.
 */

export interface TreeOrg { tenant_id: string; parent_id: string | null; name: string }

export interface TreeNode<T extends TreeOrg = TreeOrg> {
  org: T;
  depth: number;
  children: TreeNode<T>[];
  /** It and every organization under it. */
  ids: string[];
}

export type Check = 'checked' | 'unchecked' | 'partial';

/** The tree, roots first, siblings by name; an organization whose parent is
    not in the list is a root. A cycle in the data never loops. */
export function buildTree<T extends TreeOrg>(orgs: T[]): TreeNode<T>[] {
  const known = new Set(orgs.map(o => o.tenant_id));
  const children = new Map<string | null, T[]>();
  for (const o of orgs) {
    const parent = o.parent_id && known.has(o.parent_id) ? o.parent_id : null;
    children.set(parent, [...(children.get(parent) ?? []), o]);
  }
  const seen = new Set<string>();
  const make = (parent: string | null, depth: number): TreeNode<T>[] =>
    [...(children.get(parent) ?? [])]
      .sort((a, b) => a.name.localeCompare(b.name))
      .filter(o => !seen.has(o.tenant_id) && (seen.add(o.tenant_id), true))
      .map(o => {
        const kids = make(o.tenant_id, depth + 1);
        return { org: o, depth, children: kids, ids: [o.tenant_id, ...kids.flatMap(k => k.ids)] };
      });
  return make(null, 0);
}

/** Every node, parents before children — the order rows are drawn in. */
export function flatten<T extends TreeOrg>(nodes: TreeNode<T>[]): TreeNode<T>[] {
  return nodes.flatMap(n => [n, ...flatten(n.children)]);
}

export function check(node: TreeNode, chosen: ReadonlySet<string>): Check {
  const n = node.ids.filter(id => chosen.has(id)).length;
  return n === 0 ? 'unchecked' : n === node.ids.length ? 'checked' : 'partial';
}

/** Ticking a box: a ticked one is cleared with everything under it; an
    empty or partial one is filled with everything under it. */
export function toggle(node: TreeNode, chosen: ReadonlySet<string>): Set<string> {
  const next = new Set(chosen);
  if (check(node, chosen) === 'checked') node.ids.forEach(id => next.delete(id));
  else node.ids.forEach(id => next.add(id));
  return next;
}

/** "3 of 5" for a branch: how many of it and the organizations under it are chosen. */
export function branchCount(node: TreeNode, chosen: ReadonlySet<string>): { chosen: number; total: number } {
  return { chosen: node.ids.filter(id => chosen.has(id)).length, total: node.ids.length };
}

/** What a search shows: the matching organizations, every organization above
    a match (its context), and which to open so the matches are in view. An
    empty query shows everything and opens nothing. */
export function search<T extends TreeOrg>(nodes: TreeNode<T>[], query: string):
  { visible: Set<string>; matches: Set<string>; open: Set<string> } {
  const q = query.trim().toLocaleLowerCase();
  const all = flatten(nodes);
  if (!q) return { visible: new Set(all.map(n => n.org.tenant_id)), matches: new Set(), open: new Set() };
  const visible = new Set<string>();
  const matches = new Set<string>();
  const open = new Set<string>();
  const walk = (node: TreeNode<T>, above: string[]): boolean => {
    const own = node.org.name.toLocaleLowerCase().includes(q);
    let below = false;
    for (const child of node.children) below = walk(child, [...above, node.org.tenant_id]) || below;
    if (own) matches.add(node.org.tenant_id);
    if (own || below) {
      visible.add(node.org.tenant_id);
      above.forEach(id => { visible.add(id); open.add(id); });
      if (below) open.add(node.org.tenant_id);
    }
    return own || below;
  };
  nodes.forEach(n => walk(n, []));
  return { visible, matches, open };
}

/** The rows a tree draws: those the search keeps whose every organization
    above is open — opened by hand (`isOpen`) or by the search. */
export function shownNodes<T extends TreeOrg>(rows: TreeNode<T>[], found: { visible: Set<string>; open: Set<string> },
                                              isOpen: (id: string) => boolean): TreeNode<T>[] {
  const byId = new Map(rows.map(r => [r.org.tenant_id, r]));
  const inView = (node: TreeNode<T>): boolean => {
    for (let p = node.org.parent_id; p && byId.has(p); p = byId.get(p)!.org.parent_id) {
      if (!isOpen(p) && !found.open.has(p)) return false;
    }
    return true;
  };
  return rows.filter(n => found.visible.has(n.org.tenant_id) && inView(n));
}

/** The organizations a new choice takes the connector from: chosen before, not now. */
export function removedBy(before: ReadonlySet<string>, after: ReadonlySet<string>): string[] {
  return [...before].filter(id => !after.has(id));
}

/** Whether two choices are the same set. */
export function sameChoice(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  return a.size === b.size && [...a].every(id => b.has(id));
}
